import { Button, Card, CardHeader } from '@mui/material';
import axios, { AxiosError } from 'axios';
import { useContext, useState } from 'react';
import constants from '../../config/constants';
import { AlertContext } from '../../context/alert.context';
import { useLanguage } from '../../context/language.context';
import useFetch from '../../hooks/useFetch';
import { IExpert, IPrivateService } from '../../types';
import { getLocalizedErrorMessage } from '../../utils/functions';
import AppointmentScheduleDialog from '../AppointmentScheduleDialog';
import FutureAppointmentsDialog from '../FutureAppointmentsDialog';
import NewMemberSessionDialog from '../NewMemberSessionDialog';
import PrivateServicesTable from '../PrivateServicesTable';
import PrivateSessionsTable from '../PrivateSessionsTable';

const OneToOneSessionsTab = ({ profileData }: { profileData?: IExpert }) => {
    const privateServices = useFetch<IPrivateService[]>(`${constants.API_URL}/instructor/admin/private_services`);

    const privateSessions = useFetch<any[]>(`${constants.API_URL}/instructor/admin/private_appointments`);
    const alert = useContext(AlertContext);
    const { t, dir } = useLanguage();
    const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
    const [showAppointmentDialogData, setShowAppointmentDialogData] = useState<{
        name: string;
        service: any;
        member_id: string;
        loading?: boolean;
    }>({
        loading: false,
        member_id: '',
        name: '',
        service: undefined,
    });
    // GH-539/625: separate state for the "several future appointments" dialog.
    const [showFutureDialog, setShowFutureDialog] = useState(false);
    const [futureDialogData, setFutureDialogData] = useState<{
        name: string;
        service: any;
        member_id: string;
        loading?: boolean;
    }>({ loading: false, member_id: '', name: '', service: undefined });
    // GH-688: book a FIRST session for a member who never booked this expert — resolved by email,
    // then handed to the same AppointmentScheduleDialog as an existing member.
    const [showNewMemberDialog, setShowNewMemberDialog] = useState(false);
    return (
        <>
            <NewMemberSessionDialog
                open={showNewMemberDialog}
                services={privateServices.data || []}
                onClose={() => setShowNewMemberDialog(false)}
                onMemberResolved={(member_id, member_name, service) => {
                    setShowNewMemberDialog(false);
                    setShowAppointmentDialogData({ name: member_name, service, member_id, loading: false });
                    setShowAppointmentDialog(true);
                }}
            />
            <FutureAppointmentsDialog
                // remount per member so a fresh open never inherits the previous rows
                key={`future-${futureDialogData.member_id}`}
                open={showFutureDialog}
                loading={futureDialogData.loading}
                title={`קביעת פגישות עתידיות עם ${futureDialogData.name}`}
                duration={futureDialogData.service?.duration || 20}
                service={futureDialogData.service}
                onClose={() => setShowFutureDialog(false)}
                onScheduleButtonPressed={async (appointments) => {
                    setFutureDialogData((prev) => ({ ...prev, loading: true }));
                    let created = 0;
                    let lastError = '';
                    for (const appt of appointments) {
                        try {
                            await axios.post(`${constants.API_URL}/instructor/admin/private_session`, {
                                member_id: futureDialogData.member_id,
                                service_id: futureDialogData.service._id,
                                time: appt.time,
                                follow_up_accepted: false,
                                coupon: appt.coupon,
                                is_future: true, // deferred payment — form sent ~48h before each session
                            });
                            created++;
                        } catch (error) {
                            lastError = getLocalizedErrorMessage(error as AxiosError, 'he') || '';
                        }
                    }
                    const failed = appointments.length - created;
                    if (failed === 0) {
                        setShowFutureDialog(false);
                        alert?.showAlert(`נקבעו ${created} פגישות בהצלחה`);
                    } else if (created > 0) {
                        alert?.showAlert(`נקבעו ${created} פגישות, ${failed} נכשלו${lastError ? ` — ${lastError}` : ''}`, 'error');
                    } else {
                        alert?.showAlert(lastError || 'יצירת הפגישות נכשלה', 'error');
                    }
                    privateSessions.refetch();
                    setFutureDialogData((prev) => ({ ...prev, loading: false }));
                }}
            />
            <AppointmentScheduleDialog
                onClose={() => {
                    setShowAppointmentDialog(false);
                }}
                open={showAppointmentDialog}
                loading={showAppointmentDialogData.loading}
                title={`${t('scheduleAppointmentWith')} ${showAppointmentDialogData.name}`}
                duration={showAppointmentDialogData.service?.duration || 20}
                service={showAppointmentDialogData.service}
                onScheduleAppointmentButtonPressed={async (selectedTime: Date, isFollowup: boolean, coupon?: string) => {
                    setShowAppointmentDialogData((prev) => ({ ...prev, loading: true }));
                    try {
                        await axios.post(`${constants.API_URL}/instructor/admin/private_session`, {
                            member_id: showAppointmentDialogData.member_id,
                            service_id: showAppointmentDialogData.service._id,
                            time: selectedTime,
                            follow_up_accepted: isFollowup,
                            coupon,
                        });
                        setShowAppointmentDialog(false);

                        privateSessions.refetch();
                        alert?.showAlert(t('sessionScheduled'));
                    } catch (error) {
                        alert?.showAlert(getLocalizedErrorMessage(error as AxiosError, 'he'), 'error');
                    } finally {
                        setShowAppointmentDialogData((prev) => ({ ...prev, loading: false }));
                    }
                }}
            />
            <div>
                <Card dir={dir} style={{ marginTop: 20 }}>
                    <CardHeader
                        title={t('oneToOneTitle')}
                        action={
                            <Button variant='outlined' size='small' style={{ margin: 8 }} onClick={() => setShowNewMemberDialog(true)}>
                                {t('newMemberSessionButton')}
                            </Button>
                        }
                    />
                    <PrivateSessionsTable
                        refetchFunction={privateSessions.refetch}
                        isPsychologist={profileData?.is_psychologist}
                        privateSessions={privateSessions.data}
                        loading={privateSessions.loading}
                        onPrivateSessionSetAppointment={(member_id: string, member_name: string, service: any) => {
                            setShowAppointmentDialogData({ name: member_name, service, member_id, loading: false });
                            setShowAppointmentDialog(true);
                        }}
                        onScheduleFutureAppointments={(member_id: string, member_name: string, service: any) => {
                            setFutureDialogData({ name: member_name, service, member_id, loading: false });
                            setShowFutureDialog(true);
                        }}
                    />
                </Card>
            </div>
        </>
    );
};

export default OneToOneSessionsTab;
